import type { RedirectMechanismProps } from '@apps/guest/routes/HotelBooking/HotelBooking.types';
import { RedirectMechanismType } from '@graphql/generated-base';
import type { TravelOfferProvider } from '@graphql/generated-base';

/**
 * Extracts the primary redirect mechanism (excluding 'partnerSearch') and the interceptor redirect (if any).
 *
 * @param redirectMechanisms - Array of redirect mechanism objects.
 * @returns An object containing the primary redirect and the interceptor redirect, if present.
 */
export function extractHotelRedirects(redirectMechanisms?: ReadonlyArray<RedirectMechanismProps> | null): {
  hotelPageUrl?: RedirectMechanismProps;
  hotelSearchPageUrl?: RedirectMechanismProps;
} {
  if (!redirectMechanisms) return { hotelPageUrl: undefined, hotelSearchPageUrl: undefined };
  return redirectMechanisms.reduce<{
    hotelPageUrl?: RedirectMechanismProps;
    hotelSearchPageUrl?: RedirectMechanismProps;
  }>((acc, redirect) => {
    if (redirect.type === RedirectMechanismType.partnerSearch) {
      acc.hotelSearchPageUrl ??= redirect;
    } else if (!acc.hotelPageUrl) {
      acc.hotelPageUrl = redirect;
    }
    return acc;
  }, {});
}

/**
 * Returns the telemetry destination name from the selected redirect mechanism.
 * Uses the same selection logic as useAccommodationRedirectUrl: prioritizes partnerSearch
 * over other mechanisms to ensure telemetry matches the actual redirect target.
 */
export function getSelectedRedirectTelemetryName(redirectMechanisms?: ReadonlyArray<RedirectMechanismProps> | null): string {
  const { hotelSearchPageUrl, hotelPageUrl } = extractHotelRedirects(redirectMechanisms);
  const selectedMechanism = hotelSearchPageUrl || hotelPageUrl;
  return selectedMechanism?.telemetryDestinationName || '(none)';
}

/**
 * Returns the provider whose rate the selected redirect books, or undefined when it books no rate.
 *
 * Uses the same selection as getSelectedRedirectTelemetryName. Both then describe the redirect the
 * guest actually takes. Undefined is the right answer, not a gap. A partnerSearch redirect opens
 * Joy's hotel search. No provider rate backs the tile the guest clicked.
 */
export function getSelectedRedirectTravelOfferProvider(redirectMechanisms?: ReadonlyArray<RedirectMechanismProps> | null): TravelOfferProvider | undefined {
  const { hotelSearchPageUrl, hotelPageUrl } = extractHotelRedirects(redirectMechanisms);
  const selectedMechanism = hotelSearchPageUrl || hotelPageUrl;
  return selectedMechanism?.travelOfferProvider ?? undefined;
}
